// A PO's receiving side: whether its Shopify items show as incoming in Shopify (and Retry when Shopify
// refused), what's been received against it, and a way to start counting a delivery.
import { ExternalLink, PackageCheck, RotateCw } from "lucide-react";
import type { Po } from "../../../../shared/db.gen.ts";
import { canReceive } from "../../../../shared/receiving.ts";
import { can, mutate } from "../../lib/mutate.ts";
import { navigate } from "../../lib/router.tsx";
import { useRows } from "../../lib/sync.ts";
import { Badge, Button } from "../../ui/controls.tsx";
import { ReceiptList } from "../receiving/ReceiptList.tsx";

const transferUrl = (id: string) => `https://admin.shopify.com/store/hand-held-legend/transfers/${id.split("/").pop()}`;

/** "Incoming in Shopify", "Sending to Shopify…" or "Shopify refused" next to the PO's status. */
export function IncomingBadge({ po }: { po: Po }) {
  if (po.shopify_sync === "none") return null;
  if (po.shopify_sync === "failed") return <Badge tone="danger">Not in Shopify</Badge>;
  if (po.shopify_sync === "pending") return <Badge tone="warn">Sending to Shopify…</Badge>;
  return po.status === "cancelled" || po.status === "received" || po.status === "closed" ? null : <Badge tone="accent">Incoming in Shopify</Badge>;
}

export function PoReceiving({ po }: { po: Po }) {
  const receipts = useRows("receipt")
    .filter((r) => r.po_id === po.id)
    .sort((a, b) => b.received_at.localeCompare(a.received_at));
  const push = () => mutate("po.push_incoming", { id: po.id });

  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <div className="flex flex-wrap items-center gap-3 border-b border-line px-4 py-3">
        <div className="min-w-0 flex-1">
          <div className="font-medium">Shopify</div>
          <div className="text-[12px] text-muted">
            {po.shopify_sync === "done" && po.shopify_transfer_id ? (
              <>
                {po.status === "cancelled"
                  ? "Its incoming transfer was cancelled in Shopify."
                  : "Its Shopify items show as incoming at HHL; receipts post to it."}{" "}
                <a
                  href={transferUrl(po.shopify_transfer_id)}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-0.5 text-accent-text hover:underline"
                >
                  Open the transfer <ExternalLink className="size-3" />
                </a>
              </>
            ) : po.shopify_sync === "pending" ? (
              "Sending to Shopify…"
            ) : po.shopify_sync === "failed" ? (
              <span className="text-danger">{po.shopify_sync_error || "Shopify refused it."}</span>
            ) : po.status === "draft" ? (
              "Its Shopify items show as incoming in Shopify once it's marked as sent."
            ) : (
              "Not in Shopify (no Shopify items going to a Shopify location, or sent before receiving existed)."
            )}
          </div>
        </div>
        {can("po.push_incoming") && po.status !== "draft" && po.status !== "cancelled" && po.shopify_sync !== "pending" && po.shopify_sync !== "done" && (
          <Button size="sm" icon={<RotateCw className="size-3.5" />} onClick={push}>
            {po.shopify_sync === "failed" ? "Retry" : "Show as incoming in Shopify"}
          </Button>
        )}
        {canReceive(po.status) && can("receipt.post") && (
          <Button size="sm" variant="primary" icon={<PackageCheck className="size-3.5" />} onClick={() => navigate(`/receiving?po=${po.id}`)}>
            Receive a delivery
          </Button>
        )}
      </div>
      <h3 className="px-4 pt-3 pb-1 text-[11px] font-medium tracking-wide text-faint uppercase">Receipts {receipts.length || ""}</h3>
      <ReceiptList receipts={receipts} limit={200} />
    </div>
  );
}
